import { ChangeEvent, useMemo, useState } from "react";

// eslint-disable-next-line deprecate/import
import { m7DayJs } from "@m7-health/shared-utils";
import { filter, get, keys, sortBy } from "lodash";

import { ExpandLess, MenuBook } from "@mui/icons-material";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import { Box, Stack, TablePagination, Tooltip, Typography } from "@mui/material";

import {
  NoteUpdated,
  StaffShiftCreated,
  StaffShiftDeleted,
  StaffShiftUpdated,
} from "~/api/eventLog/events";

import { SchedulerGridStore } from "#/features/SchedulerGrid/store";
import { EventLog, IEventLog } from "@/api";
import { StaffShiftHistoryModal } from "@/common/components";
import { Filters } from "@/common/components/StaffShiftHistoryModal/Filters";
import { useAppDispatch } from "@/common/hooks";
import { black, lightGray, mediumGray } from "@/common/theming/colors";

import { AppLoader } from "../AppLoader/AppLoader";

import { componentForEvent, IEventLogComponent } from "./events";
import { EVENT_TITLES, HumanizedTimestamp, IconForEvent } from "./helpers";
import { ModalEvents, TChangeReportSidebarFilters, TTarget } from "./types";
import { useDataForShift } from "./useDataForShift";

const ShiftEvents = new Set([
  EventLog.EEventType.staffShiftCreated,
  EventLog.EEventType.staffShiftUpdated,
  EventLog.EEventType.staffShiftDeleted,
]);
const EventsToShow = new Set([...ShiftEvents, EventLog.EEventType.noteUpdated]);

type TStaffShiftEventType =
  | StaffShiftCreated.Payloads
  | StaffShiftDeleted.Payloads
  | StaffShiftUpdated.Payloads;
type TStaffShiftEvent = EventLog.DTO<TStaffShiftEventType>;
type TNoteEvent = EventLog.DTO<NoteUpdated.Payloads>;

const getByDate = (logs?: EventLog.DTO[]) => {
  if (!logs) {
    return undefined;
  }

  return logs.reduce(
    (hash, event) => {
      const updatedAt = event.eventEmittedAt.substring(0, 10);

      if (!hash[updatedAt]) {
        hash[updatedAt] = [];
      }
      hash[updatedAt]!.push(event);

      return hash;
    },
    {} as Record<string, EventLog.DTO[]>,
  );
};

export const StaffShiftHistoryContent = ({
  target,
  currentUnitId,
  onClickEvent,
}: {
  target: TTarget;
  currentUnitId?: string;
  onClickEvent?: (event: IEventLog) => void;
}) => {
  const dispatch = useAppDispatch();
  const [shiftHistoryIsOpen, setShiftHistoryIsOpen] = useState(false);

  const {
    logs,
    pagination,
    usersById,
    indexedShiftTypes,
    unitIdByScheduleId,
    isLoading,
    availableSchedules,
  } = useDataForShift(
    {
      ...target,
    } as TTarget,
    currentUnitId,
  );

  const setFiltersUpdate = (filtersUpdated: Partial<TChangeReportSidebarFilters | undefined>) => {
    target.changeReportSidebarFilters &&
      dispatch(
        SchedulerGridStore.setChangeReportFilters({
          ...target.changeReportSidebarFilters,
          ...filtersUpdated,
        }),
      );
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPageIndex: number,
  ) => {
    setFiltersUpdate({ skip: newPageIndex * (target.changeReportSidebarFilters?.take ?? 100) });
  };

  const handleRowsPerPageChange = ({
    target: { value },
  }: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const rowsPerPage = Number(value);
    if (isNaN(rowsPerPage)) {
      return;
    }

    setFiltersUpdate({ take: rowsPerPage, skip: 0 });
  };

  // For now constant, only show draft. We might want to change this in the future
  const currentScheduleType = "draft";

  const logsToShow = useMemo(() => {
    const filteredLogs = filter(logs, (event) => {
      // show all events when scheduleIds provided
      if (target.changeReportSidebarFilters) {
        return true;
      }

      if (!EventsToShow.has(event.eventType)) return false;

      if (ShiftEvents.has(event.eventType)) {
        const scheduleType = (event as TStaffShiftEvent).eventPayload.staffShift.scheduleType;

        if (!scheduleType || scheduleType !== currentScheduleType) return false;
        const isUpdatedEvent = event.eventType === EventLog.EEventType.staffShiftUpdated;
        const hasDiff = keys(get(event, "eventPayload.updatedFields"))?.length;
        if (isUpdatedEvent && !hasDiff) return false;
        return true;
      }

      // Only keep events that belongs to current shown user
      if (event.eventType === EventLog.EEventType.noteUpdated) {
        if (!target.userId) return false;
        return (event as TNoteEvent).eventPayload.note.userId === target.userId;
      }

      return false;
    }) as (TNoteEvent | TStaffShiftEvent)[];

    const sortedLogs = sortBy(filteredLogs, ({ eventEmittedAt }) => eventEmittedAt).reverse();

    return target.changeReportSidebarFilters ? filteredLogs : sortedLogs;
  }, [logs, target.changeReportSidebarFilters, target.userId]);

  const logsToShowByDate = getByDate(logsToShow);

  return (
    <>
      <Stack
        sx={{
          height: "calc(100% - 54px)",
          overflowY: "hidden",
        }}
      >
        <Box sx={{ marginBottom: "6px" }}>
          {target.changeReportSidebarFilters && (
            <Filters
              filters={target.changeReportSidebarFilters}
              setFilters={setFiltersUpdate}
              availableSchedules={availableSchedules}
            />
          )}
        </Box>

        {isLoading ? (
          <AppLoader open={true} />
        ) : logsToShow.length ? (
          <>
            <Box
              px={1}
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                position: "relative",
              }}
            >
              <Timeline sx={leftAlignedTimelineCss}>
                {Object.keys(logsToShowByDate ?? {}).map((key) => {
                  return (
                    <>
                      <Box
                        className="date-cell"
                        sx={{
                          position: "sticky",
                          top: "0px",
                          backgroundColor: lightGray,
                          padding: "0.7rem 1rem",
                          margin: "0.5rem 0",
                          zIndex: 1,
                        }}
                      >
                        <Typography>{m7DayJs(key).format("MMM D")}</Typography>
                      </Box>
                      {(logsToShowByDate?.[key] ?? []).map((eventLog, index) => (
                        <TimelineItem
                          key={eventLog.id}
                          sx={{ p: 0 }}
                          className={`event-timeline-item ${eventLog.eventType}`}
                        >
                          {index === 0 && (
                            <ExpandLess
                              sx={{
                                position: "absolute",
                                left: "6px",
                                top: "-8px",
                                color: "rgb(189, 189, 189)",
                              }}
                            />
                          )}
                          {/* Line + icon */}
                          <TimelineSeparator>
                            <TimelineConnector />
                            <TimelineDot>
                              <IconForEvent eventLog={eventLog} />
                            </TimelineDot>
                            <TimelineConnector />
                          </TimelineSeparator>

                          {/* Actual content of the event (beside the line) */}
                          <TimelineContent sx={{ py: "12px", px: 2 }}>
                            {/* Title + timestamp */}
                            <Stack direction="row" alignItems="center">
                              <Typography component="span">
                                {EVENT_TITLES[eventLog.eventType as ModalEvents] || "Unknown event"}
                                {!target.staffShiftId &&
                                  ShiftEvents.has(eventLog.eventType) &&
                                  !target.changeReportSidebarFilters && (
                                    <>
                                      <Tooltip title="Browse shift full history">
                                        <MenuBook
                                          fontSize="small"
                                          sx={{
                                            color: mediumGray,
                                            "&:hover": { color: black },
                                            ml: 1,
                                          }}
                                          onClick={(event) => {
                                            setShiftHistoryIsOpen(true);
                                            event.stopPropagation();
                                          }}
                                        />
                                      </Tooltip>
                                      {shiftHistoryIsOpen && (
                                        <StaffShiftHistoryModal
                                          staffShiftId={
                                            (eventLog as TStaffShiftEvent).eventPayload.staffShift
                                              .id
                                          }
                                          onClose={() => setShiftHistoryIsOpen(false)}
                                          modalTitle="Shift History"
                                          withBlurBackground={false}
                                        />
                                      )}
                                    </>
                                  )}
                              </Typography>
                              <Box sx={{ flexGrow: 1 }} />
                              <HumanizedTimestamp date={eventLog.eventEmittedAt} />
                            </Stack>

                            {/* Description */}
                            <Typography
                              display="inline-block"
                              onClick={() => onClickEvent?.(eventLog)}
                              sx={
                                onClickEvent !== undefined
                                  ? {
                                      cursor: "pointer",
                                      "&:hover": { backgroundColor: mediumGray },
                                    }
                                  : {}
                              }
                            >
                              {descriptionForEventLog({
                                event: eventLog,
                                usersById,
                                indexedShiftTypes,
                                unitIdByScheduleId,
                              })}
                            </Typography>
                          </TimelineContent>
                        </TimelineItem>
                      ))}
                    </>
                  );
                })}
              </Timeline>
            </Box>
            <Box>
              {pagination && target.changeReportSidebarFilters && (
                <TablePagination
                  component="div"
                  count={pagination.total}
                  page={
                    target.changeReportSidebarFilters.skip / target.changeReportSidebarFilters.take
                  }
                  onPageChange={handlePageChange}
                  rowsPerPage={target.changeReportSidebarFilters.take}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  sx={{
                    borderTop: `1px solid ${mediumGray}`,
                    ".MuiTablePagination-toolbar": {
                      px: 0,
                      columnGap: "10px",
                      justifyContent: "flex-end",
                    },
                    ".MuiTablePagination-toolbar > *": { mx: "0!important" },
                    ".MuiTablePagination-spacer": { display: "none" },
                  }}
                />
              )}
            </Box>
          </>
        ) : (
          <Typography variant="body1" p={2}>
            No history found
          </Typography>
        )}
      </Stack>
    </>
  );
};

const descriptionForEventLog = (params: IEventLogComponent) => {
  const EventComponent = componentForEvent(params.event);
  if (!EventComponent) return `Unknown event ${params.event.eventType}`;

  return <EventComponent {...params} />;
};

const leftAlignedTimelineCss = {
  marginTop: 0,
  p: 0,
  [`& .${timelineItemClasses.root}:before`]: {
    flex: 0,
    padding: 0,
  },
};
